import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import CampaignService from "@app/services/http/campaign.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta, Campaign, TypeToggleStatus } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Checkbox from "@app/components/checkbox";

function DiscountColumnTable({ data }: { data: Campaign }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div>{data.discount} %</div>
    </div>
  );
}

function TimeColumnTable({ data }: { data: Campaign }) {
  const currentDate = new Date();
  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);

  let status;

  if (currentDate < startDate) {
    status = "Chưa bắt đầu";
  } else if (currentDate >= startDate && currentDate <= endDate) {
    status = "Đang diễn ra";
  } else {
    status = "Đã kết thúc";
  }

  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <div className="text-sm text-gray-500">{status}</div>
    </div>
  );
}

// moved inside component to access forceUpdate

function TestingIdsColumn({ data }: { data: Campaign }) {
  const value = Array.isArray(data.testingIds)
    ? data.testingIds.join(", ")
    : data.testingIds || "-";

  return (
    <div className="truncate min-w-[120px] max-w-[240px]">{String(value)}</div>
  );
}

const CampaignTable = () => {
  const [campaign, setCampaogn] = useState<Campaign[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [update, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();
  const navigate = useNavigate();

  const ActiveCheckbox = ({ data }: { data: Campaign }) => {
    const handleToggle = (e: any) => {
      e.stopPropagation();

      CampaignService.toggleStatus(data.id, TypeToggleStatus.CAMPAIGN).then(
        () => {
          forceUpdate();
        }
      );
    };

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={Boolean(data.isActive)}
          onChange={handleToggle}
          checkboxClassName="w-4 h-4 cursor-pointer"
        />
      </div>
    );
  };

  const columnConfig: ColumnConfig[] = [
    {
      label: "Campaign",
      dataKey: "name",
      width: "500px",
      tdClassName: "truncate min-w-[150px] max-w-[350px]",
      hasOrder: true,
    },
    {
      label: "Giảm giá",
      dataKey: "discount",
      width: "500px",
      hasOrder: true,
      component: DiscountColumnTable,
    },
    {
      label: "Thời gian",
      dataKey: "time",
      width: "30%",
      component: TimeColumnTable,
      hasOrder: true,
    },
    {
      label: "Trạng thái",
      dataKey: "isActive",
      width: "12%",
      component: ({ data }: { data: Campaign }) => (
        <ActiveCheckbox data={data} />
      ),
      hasOrder: true,
    },
    {
      label: "Id test",
      dataKey: "testingIds",
      width: "18%",
      component: TestingIdsColumn,
      hasOrder: false,
    },
  ];

  useEffect(() => {
    CampaignService.getList(requestParams, abortController.signal).then(
      (res) => {
        setCampaogn(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleDeleteClick = (campaignId: number) => {
    if (campaignId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_CAMPAIGN_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          CampaignService.delete(campaignId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });

            forceUpdate();
          });
        }
      });
    }
  };

  const handleEditClick = (campaign: Campaign) => {
    navigate(
      `/${RoutePath.CAMPAIGN_MANAGEMENT}/${RoutePath.UPDATE_CAMPAIGN}/${campaign.id}?type=campaigns`
    );
  };

  return (
    <div className="mt-6 rounded-xl bg-white py-6 px-5">
      <TitleDescription
        title="Danh sách campaign"
        onInputChange={onSearchChange}
      />
      <div className="mt-6">
        <Table
          columnConfig={columnConfig}
          dataTable={campaign}
          hasAction
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          meta={meta}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onRowClickOrder={onOrderChange}
          componentEmpty={
            <div className="text-center py-3 bg-[#f4f4f4]">
              <p className="text-[#666] text-xl">Không có dữ liệu</p>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default CampaignTable;
